'use client'

import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import openSocket, { Socket } from 'socket.io-client'

import { getWaitingForHuman } from '@/lib/api/atendimento/chat'
import { UserType } from '@/lib/api/common/interfaces/user.interface'
import { getUser } from '@/lib/api/users/users-api'
import { getClientLabelCapitalized } from '@/lib/form-state/util'
import { usePermission } from '@/lib/hooks/use-permission'
import { useAppDispatch, useAuth } from '@/store/hooks'
import { logout, setCredentials } from '@/store/slices/authSlice'
import { refreshAccessToken } from '@/lib/api/session-refresh'
import {
    addWaitingForHuman,
    clearAll,
    clearWaitingForHuman,
    removeWaitingForHuman,
} from '@/store/slices/notificationsSlice'
import { createContext } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { isAiUserType } from '@/lib/auth/restricted-viewer'

const baseUrl = import.meta.env.VITE_API_BASE_URL

if (process.env.NODE_ENV === 'development') {
    console.log('[Socket] Base URL:', baseUrl)
}

if (!baseUrl) {
    console.error('[Socket] REACT_APP_API_BASE_URL não está definida!')
}

const INITIAL_RETRY_DELAY = 1000
const MAX_RETRY_DELAY = 30000

interface SocketProps {
    socket: Socket
    isConnected: boolean
    isConnecting: boolean
}

export const SocketContext = createContext<SocketProps>({
    socket: null as any,
    isConnected: false,
    isConnecting: false,
})

function connection(token: string) {
    if (!baseUrl) {
        console.error('[Socket] Não é possível conectar: baseUrl não definida')
        throw new Error('REACT_APP_API_BASE_URL não está definida')
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('[Socket] Conectando em:', baseUrl)
    }

    return openSocket(baseUrl, {
        transports: ['websocket', 'polling'],
        auth: {
            token,
        },
        reconnectionDelay: INITIAL_RETRY_DELAY,
        reconnectionDelayMax: MAX_RETRY_DELAY,
        randomizationFactor: 0.5,
        reconnection: true,
        reconnectionAttempts: Infinity,
    })
}

export default function SocketProvider({ children }: PropsWithChildren) {
    const [socket, setSocket] = useState<Socket>()
    const [isConnected, setIsConnected] = useState(false)
    const [isConnecting, setIsConnecting] = useState(false)
    const dispatch = useAppDispatch()
    const location = useLocation()

    const auth = useAuth()
    const { user, isAuthenticated, token, refreshToken } = auth
    const authRef = useRef(auth)
    const { canRead } = usePermission()
    const canReadChannels = canRead('atendimento', 'channels')

    useEffect(() => {
        authRef.current = auth
    }, [auth])

    useEffect(() => {
        if (!isAuthenticated || !token) {
            if (socket) {
                socket.disconnect()
                setSocket(undefined)
            }
            setIsConnected(false)
            setIsConnecting(false)
            dispatch(clearAll())
            return
        }

        const initializeSocket = () => {
            setIsConnecting(true)

            const newSocket = connection(token)

            if (newSocket && newSocket.connected) {
                setIsConnected(true)
                setIsConnecting(false)
                setSocket(newSocket)
                return newSocket
            }

            if (!newSocket) {
                return
            }

            newSocket.on('connect', async () => {
                setIsConnected(true)
                setIsConnecting(false)
                if (process.env.NODE_ENV === 'development') {
                    console.log('Socket connected')
                }

                // O backend reinicia no deploy, então toda reconexão é forte
                // indício de que subiu versão nova. É o gatilho mais preciso que
                // existe — chega no instante do deploy, sem relógio nenhum
                // batendo no servidor. Ver `lib/app-version/`.
                window.dispatchEvent(new Event('app:socket-reconnected'))

                dispatch(clearAll())

                if (user?.company?.id) {
                    if (canReadChannels) {
                        try {
                            const response = await getWaitingForHuman()
                            if (response.success && response.result) {
                                dispatch(clearWaitingForHuman())
                                response.result.forEach(chat => {
                                    dispatch(
                                        addWaitingForHuman({
                                            chatId: chat.id,
                                            phoneNumber: chat.phoneNumber,
                                            name: chat.name,
                                            reason: chat.reason,
                                            timestamp: chat.updatedAt,
                                        })
                                    )
                                })
                                if (process.env.NODE_ENV === 'development') {
                                    console.log(
                                        `[WaitingForHuman] Sincronizados ${response.result.length} atendimentos aguardando`
                                    )
                                }
                            }
                        } catch (error) {
                            console.error(
                                '[WaitingForHuman] Erro ao sincronizar:',
                                error
                            )
                        }
                    }
                }
            })

            // Payment response listener
            if (user?.company?.id) {
                const paymentEventName = `payment-response-${user.company.id}`

                newSocket.on(paymentEventName, (data: any) => {
                    if (process.env.NODE_ENV === 'development') {
                        console.log(
                            '[PaymentResponse] Webhook data received:',
                            data
                        )
                    }

                    // Se não estiver na página de checkout, exibir toast. O
                    // checkout hoje é o público da cobrança (`/pay/:token`), que
                    // mostra a confirmação na própria tela — o toast só faria
                    // sentido para quem está em outro lugar do sistema.
                    const isCheckoutPage = location.pathname.includes('/pay/')

                    if (!isCheckoutPage) {
                        if (
                            data.payment?.status === 'CONFIRMED' ||
                            data.payment?.status === 'RECEIVED'
                        ) {
                            toast.success('Pagamento confirmado com sucesso!')
                        } else if (
                            data.payment?.status === 'OVERDUE' ||
                            data.payment?.status === 'REFUNDED'
                        ) {
                            toast.error('Falha no pagamento da assinatura')
                        } else if (data.payment?.status === 'PENDING') {
                            toast.info(
                                'Pagamento pendente. Aguardando confirmação.'
                            )
                        }
                    }

                    if (
                        data.payment?.status === 'CONFIRMED' ||
                        data.payment?.status === 'RECEIVED'
                    ) {
                        if (user?.id && token) {
                            getUser(user.id)
                                .then(response => {
                                    if (response.success && response.result) {
                                        const updatedUser = response.result

                                        dispatch(
                                            setCredentials({
                                                user: updatedUser,
                                                token: token,
                                                refreshToken:
                                                    refreshToken || '',
                                                isAuthenticated: true,
                                                isMaster:
                                                    updatedUser.userType ===
                                                        UserType.MASTER &&
                                                    updatedUser.role?.name ===
                                                        'master',
                                                isCompany:
                                                    updatedUser.userType ===
                                                        UserType.COMPANY &&
                                                    updatedUser.role?.name ===
                                                        'company',
                                                isCustom:
                                                    updatedUser.userType ===
                                                    UserType.CUSTOM,
                                                isEmployee:
                                                    updatedUser.userType ===
                                                        UserType.EMPLOYEE &&
                                                    updatedUser.role?.name ===
                                                        'employee',
                                                isPartner:
                                                    updatedUser.userType ===
                                                    UserType.PARTNER,
                                                isProfessional:
                                                    updatedUser.userType ===
                                                        UserType.PROFESSIONAL &&
                                                    updatedUser.role?.name ===
                                                        'professional',
                                                accessibleCompanies:
                                                    updatedUser.accessibleCompanies ||
                                                    [],
                                            })
                                        )

                                        if (
                                            process.env.NODE_ENV ===
                                            'development'
                                        ) {
                                            console.log(
                                                '[PaymentResponse] Dados do usuário atualizados após confirmação de pagamento'
                                            )
                                        }
                                    }
                                })
                                .catch(error => {
                                    console.error(
                                        '[PaymentResponse] Erro ao atualizar dados do usuário:',
                                        error
                                    )
                                })
                        }
                    }
                })

                if (canReadChannels) {
                    const waitingForHumanEventName = `waiting-for-human-${user.company.id}`

                    newSocket.on(waitingForHumanEventName, (data: any) => {
                        if (process.env.NODE_ENV === 'development') {
                            console.log(
                                '[WaitingForHuman] New notification:',
                                data
                            )
                        }

                        dispatch(addWaitingForHuman(data))

                        const clientLabel = getClientLabelCapitalized(
                            user?.company?.segment
                        )
                        toast.info(
                            `${clientLabel} ${data.name} solicitou atendimento`,
                            {
                                autoClose: 5000,
                            }
                        )
                    })

                    const humanAttendedEventName = `human-attended-${user.company.id}`

                    newSocket.on(humanAttendedEventName, (data: any) => {
                        if (process.env.NODE_ENV === 'development') {
                            console.log(
                                '[HumanAttended] Notification removed:',
                                data
                            )
                        }

                        dispatch(removeWaitingForHuman(data.chatId))
                    })
                }
            }

            newSocket.on('force-logout', async (data: any) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log(
                        '[ForceLogout] Sessão invalidada:',
                        data?.reason
                    )
                }
                const { logoutOneSignal } =
                    await import('@/lib/utils/onesignal-logout')
                await logoutOneSignal()
                dispatch(logout())
            })

            newSocket.on('role-updated', (data: { role: any }) => {
                if (data?.role) {
                    const current = authRef.current
                    const updatedUser = { ...current.user, role: data.role }
                    dispatch(
                        setCredentials({
                            user: updatedUser,
                            token: current.token,
                            refreshToken: current.refreshToken,
                            isAuthenticated: true,
                            isMaster: updatedUser.userType === 'MASTER',
                            isCompany: updatedUser.userType === 'COMPANY',
                            isCustom: updatedUser.userType === 'CUSTOM',
                            isEmployee:
                                updatedUser.userType === 'EMPLOYEE' ||
                                isAiUserType(updatedUser.userType),
                            isPartner: updatedUser.userType === 'PARTNER',
                            isProfessional:
                                updatedUser.userType === 'PROFESSIONAL',
                            accessibleCompanies: current.accessibleCompanies,
                        })
                    )
                    if (process.env.NODE_ENV === 'development') {
                        console.log(
                            '[RoleUpdated] Permissões atualizadas em tempo real'
                        )
                    }
                }
            })

            newSocket.on('plan-updated', (data: { plan: any }) => {
                if (data?.plan) {
                    const current = authRef.current
                    const currentCompany = current.user?.company
                    if (currentCompany?.organization?.subscription) {
                        const updatedUser = {
                            ...current.user,
                            company: {
                                ...currentCompany,
                                organization: {
                                    ...currentCompany.organization,
                                    subscription: {
                                        ...currentCompany.organization
                                            .subscription,
                                        plan: data.plan,
                                    },
                                },
                            },
                        }
                        dispatch(
                            setCredentials({
                                user: updatedUser,
                                token: current.token,
                                refreshToken: current.refreshToken,
                                isAuthenticated: true,
                                isMaster: updatedUser.userType === 'MASTER',
                                isCompany: updatedUser.userType === 'COMPANY',
                                isCustom: updatedUser.userType === 'CUSTOM',
                                isEmployee:
                                updatedUser.userType === 'EMPLOYEE' ||
                                isAiUserType(updatedUser.userType),
                                isPartner: updatedUser.userType === 'PARTNER',
                                isProfessional:
                                    updatedUser.userType === 'PROFESSIONAL',
                                accessibleCompanies:
                                    current.accessibleCompanies,
                            })
                        )
                        if (process.env.NODE_ENV === 'development') {
                            console.log(
                                '[PlanUpdated] Plano atualizado em tempo real'
                            )
                        }
                    }
                }
            })

            // Aviso do sistema publicado pelo painel MASTER. O evento não traz o
            // conteúdo — quem decide o que cada pessoa pode ver é o servidor, na
            // consulta de pendentes. Aqui só se avisa a tela para ir buscar.
            newSocket.on('system-announcement-published', () => {
                window.dispatchEvent(new Event('app:announcement-published'))
            })

            newSocket.on('disconnect', () => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('Socket disconnected')
                }
                setIsConnected(false)
            })

            newSocket.on('connect_error', async error => {
                setIsConnecting(false)
                setIsConnected(false)
                if (process.env.NODE_ENV === 'development') {
                    console.log('Socket connect_error', error)
                }
                // Token expirado não é sessão perdida: renova e deixa o
                // efeito reconectar com o token novo (ele depende de `token`).
                // Só derruba se a renovação falhar.
                if (
                    error.message.includes('TOKEN_EXPIRED') ||
                    error.message.includes('TOKEN_EXPIRES')
                ) {
                    const renewed = await refreshAccessToken()
                    if (renewed) return
                }

                if (
                    error.message.includes('TOKEN_EXPIRED') ||
                    error.message.includes('TOKEN_EXPIRES') ||
                    error.message.includes('NO_AUTH_TOKEN') ||
                    error.message.includes('INVALID_TOKEN') ||
                    error.message.includes('TOKEN_MISSING') ||
                    error.message.includes('USER_NOT_FOUND') ||
                    error.message.includes('API_NOT_FOUND')
                ) {
                    const { logoutOneSignal } =
                        await import('@/lib/utils/onesignal-logout')
                    await logoutOneSignal()
                    dispatch(logout())
                }
            })

            newSocket.on('error', async (error: any) => {
                setIsConnecting(false)
                setIsConnected(false)
                if (process.env.NODE_ENV === 'development') {
                    console.log('Socket error event', error)
                }
                if (error.code === 'TOKEN_EXPIRED') {
                    const renewed = await refreshAccessToken()
                    if (renewed) return
                }

                if (
                    error.code === 'TOKEN_EXPIRED' ||
                    error.code === 'TOKEN_MISSING' ||
                    error.code === 'INVALID_TOKEN' ||
                    // Token sem o claim `type` (anterior à separação
                    // access/refresh) ou refresh usado como credencial.
                    error.code === 'INVALID_TOKEN_TYPE' ||
                    error.code === 'USER_NOT_FOUND'
                ) {
                    const { logoutOneSignal } =
                        await import('@/lib/utils/onesignal-logout')
                    await logoutOneSignal()
                    dispatch(logout())
                }
            })

            setSocket(newSocket)

            return newSocket
        }

        const newSocket = initializeSocket()

        return () => {
            if (newSocket) {
                if (user?.company?.id) {
                    newSocket.off(`payment-response-${user.company.id}`)
                    newSocket.off(`waiting-for-human-${user.company.id}`)
                    newSocket.off(`human-attended-${user.company.id}`)
                    newSocket.off(`new-order-${user.company.id}`)
                }
                newSocket.disconnect()
            }
            setIsConnected(false)
            setIsConnecting(false)
            dispatch(clearAll())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, token, user?.id, user?.company?.id])

    // Reconectar quando o app volta do background (Safari PWA)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState === 'visible' &&
                socket &&
                !socket.connected
            ) {
                socket.connect()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
        }
    }, [socket])

    return (
        <SocketContext.Provider
            value={{
                socket: socket as Socket,
                isConnected,
                isConnecting,
            }}
        >
            {children}
        </SocketContext.Provider>
    )
}
